import { SiteUrls } from '@tableau/shared-urls';
import * as React from 'react';

import { Messages } from '../../gen-src/Localize';
import { TableauUserFragment } from '../../gen-src/queries/TableauUserFragment';
import { Style } from './style/StyleFunctions';
import { SiteUrlsContext } from './urls/SiteUrlsContext';
import { WithNonNullableMembers } from './utils/TypescriptHelpers';

/**
 * Metadata for a User that we need to render it in Data Catalog's UI.
 * Depending on which fields are defined vs. null, this could represent a
 * hidden user, a deleted user, or an active user. Each client is responsible
 * for mapping the user's metadata from the API response type to this structure.
 * Assuming all the known info was provided, the `UserTextDisplayComponent`
 * and `UserLinkDisplayComponent` will figure out what kind of user it is, and
 * render accordingly.
 */
export type TableauUser = Omit<TableauUserFragment, '__typename'>;

/**
 * Hidden users will have all their fields null; we'll render "Hidden User"
 */
export type ActiveUser = WithNonNullableMembers<TableauUser>;

/**
 * Deleted users have only a name; we'll render them as text instead of links
 */
export type DeletedUser =
  { [K in keyof Omit<TableauUser, 'name'>]: null } &
  { name: NonNullable<TableauUser['name']> };


/** This matches the HiddenUserStyle in VizPortalClient */
export const UserPlaceholderStyle: React.CSSProperties & Style = {
  color: '#BFBFBF',
  fontWeight: 'inherit'
};

export function DefaultNoneUserElement(): JSX.Element {
  return <span style={UserPlaceholderStyle}>{Messages.None()}</span>;
}

export function DefaultHiddenUserElement(): JSX.Element {
  return <span style={UserPlaceholderStyle}>{Messages.HiddenUser()}</span>;
}

export function UserNameText(props: { readonly name: string }) {
  return <span>{props.name}</span>;
}


export function isUserInfoVisible(user: TableauUser): user is ActiveUser | DeletedUser {
  // Some fields may still possibly be empty string, which is still "visible"
  return user.domain !== null || user.name !== null || user.username !== null;
}

export function isUserActive(user: TableauUser): user is ActiveUser {
  return user.domain !== null && user.username !== null;
}

export function getUserNameForDisplay(user: ActiveUser | DeletedUser): string {
  // The name should always be defined for these types, but the previous
  // code fell back to username so we're preserving that logic here, just in case
  // some client code doesn't have strict null checks.
  return user.name || user.username || '';
}

export function getUserUrlForDisplay(urls: SiteUrls, user: ActiveUser): string {
  return urls.userUrl(user.username, user.domain);
}

function getUserPathForDisplay(urls: SiteUrls, user: ActiveUser): string {
  return urls.userPath(user.username, user.domain);
}


export type UserLinkProps = {
  /** The display name of the user */
  readonly name: string;

  /** The URL of that user's place page */
  readonly url: string;

  /** The path of that user's place page (for use when rendering React Router links) */
  readonly pathname: string;
};

export type UserTextDisplayComponentProps = {
  /**
   * A null user means there is no user (e.g. no contact person for a database), then the component will render "None".
   * The user object itself may also have null fields, in which case the component will render "Hidden user".
   */
  readonly user: TableauUser | null;

  /**
   * The element that should be rendered if the user is hidden. If not provided, a default element will be used.
   */
  readonly hiddenUserElement?: JSX.Element;

  /**
   * The element that should be rendered if the user is nonexistent. If not provided, a default element will be used.
   */
  readonly noneUserElement?: JSX.Element;
};

export type UserLinkDisplayComponentProps = UserTextDisplayComponentProps & {

  /**
   * The hyperlink component that should be rendered if the user is valid.
   * Not required for UserTextDisplayComponent.
   */
  readonly linkComponent: React.ComponentType<UserLinkProps>;
};

export function UserTextDisplayComponent(props: UserTextDisplayComponentProps): JSX.Element {
  if (!props.user) {
    return props.noneUserElement || <DefaultNoneUserElement />;
  } else if (!isUserInfoVisible(props.user)) {
    return props.hiddenUserElement || <DefaultHiddenUserElement />;
  } else {
    const name = getUserNameForDisplay(props.user);
    return <UserNameText name={name} />;
  }
}

export function UserLinkDisplayComponent(props: UserLinkDisplayComponentProps): JSX.Element {
  const urls = React.useContext(SiteUrlsContext);
  if (!props.user) {
    return props.noneUserElement || <DefaultNoneUserElement />;
  } else if (!isUserInfoVisible(props.user)) {
    return props.hiddenUserElement || <DefaultHiddenUserElement />;
  } else if (!isUserActive(props.user)) {
    return <UserNameText name={props.user.name} />;
  } else {
    const name = getUserNameForDisplay(props.user);
    const url = getUserUrlForDisplay(urls, props.user);
    const path = getUserPathForDisplay(urls, props.user);
    return <props.linkComponent name={name} url={url} pathname={path} />;
  }
}
